import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle2, Clock, Loader2, Bike, ShoppingBag } from "lucide-react";
import api from "../lib/api";
import { useCart, gbp } from "../context/CartContext";
import { BrandLogo } from "../components/BrandLogo";
import { Button } from "../components/ui/button";

export default function PaymentSuccess() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { clear } = useCart();
  const [status, setStatus] = useState("polling"); // polling | paid | timeout
  const [order, setOrder] = useState(null);
  const attempts = useRef(0);
  const cleared = useRef(false);

  useEffect(() => {
    const sessionId = params.get("session_id");
    if (!sessionId) { navigate("/"); return; }
    let active = true;
    const poll = async () => {
      if (!active) return;
      if (attempts.current >= 12) { setStatus("timeout"); return; }
      attempts.current += 1;
      try {
        const { data } = await api.get(`/orders/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setOrder(data.order);
          setStatus("paid");
          if (!cleared.current) { clear(); cleared.current = true; }
          return;
        }
      } catch {}
      setTimeout(poll, 2000);
    };
    poll();
    return () => { active = false; };
  }, []);

  if (status === "polling") {
    return (
      <div className="min-h-screen bg-brand-dark flex flex-col items-center justify-center text-brand-cream gap-4">
        <Loader2 className="w-10 h-10 animate-spin text-brand-teal" />
        <p className="text-brand-cream/70">Confirming your payment…</p>
      </div>
    );
  }

  if (status === "timeout") {
    return (
      <div className="min-h-screen bg-brand-dark flex flex-col items-center justify-center text-brand-cream gap-4 px-6 text-center">
        <p>We're still confirming your payment. If it doesn't appear, please contact the pub.</p>
        <Button onClick={() => navigate("/")} className="bg-brand-teal">Back home</Button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-brand-cream grain">
      <div className="bg-brand-dark text-brand-cream">
        <div className="max-w-lg mx-auto px-6 py-5"><BrandLogo dark size="text-2xl" /></div>
      </div>
      <div className="max-w-lg mx-auto px-4 py-8" data-testid="order-confirmation">
        <div className="text-center">
          <div className="w-20 h-20 rounded-full bg-brand-teal/15 flex items-center justify-center mx-auto animate-slide-up">
            <CheckCircle2 className="w-11 h-11 text-brand-teal" />
          </div>
          <h1 className="font-head text-3xl font-bold text-brand-dark mt-4">Thanks! We've got your order.</h1>
          <p className="text-brand-dark/60 mt-1">A confirmation has been emailed to you.</p>
        </div>

        <div className="bg-white rounded-3xl p-6 mt-6 shadow-sm border border-brand-dark/5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-brand-dark/40 uppercase tracking-wide">Order number</p>
              <p className="font-head text-3xl font-bold text-brand-dark" data-testid="order-number">#{order?.order_number}</p>
            </div>
            <span className="flex items-center gap-1.5 bg-brand-teal text-white px-3 py-1.5 rounded-full text-sm font-semibold capitalize">
              {order?.type === "delivery" ? <Bike className="w-4 h-4" /> : <ShoppingBag className="w-4 h-4" />} {order?.type}
            </span>
          </div>

          <div className="mt-4 flex items-center gap-2 text-brand-dark/70 bg-brand-cream rounded-xl p-3">
            <Clock className="w-4 h-4 text-brand-teal" />
            <span className="font-semibold">Estimated {order?.type}: {order?.estimated_time}</span>
          </div>

          {order?.type === "delivery" && order?.address && (
            <p className="text-sm text-brand-dark/60 mt-3">
              Delivering to: {order.address.line1}{order.address.line2 ? `, ${order.address.line2}` : ""}, {order.address.town}, {order.address.postcode}
            </p>
          )}

          <div className="border-t mt-4 pt-4 space-y-2">
            {order?.items?.map((it, i) => (
              <div key={i} className="flex justify-between text-sm">
                <div>
                  <span className="text-brand-dark/80">{it.qty}× {it.name}</span>
                  {it.extras?.map((e) => <div key={e.id} className="text-xs text-brand-dark/40">+ {e.name}</div>)}
                  {it.notes && <div className="text-xs text-red-500">Note: {it.notes}</div>}
                </div>
                <span className="font-medium">{gbp(it.line_total)}</span>
              </div>
            ))}
            {order?.subtotal != null && (
              <div className="flex justify-between text-sm text-brand-dark/60 pt-1"><span>Subtotal</span><span>{gbp(order.subtotal)}</span></div>
            )}
            {order?.delivery_charge > 0 && (
              <div className="flex justify-between text-sm text-brand-dark/60"><span>Delivery</span><span>{gbp(order.delivery_charge)}</span></div>
            )}
            {order?.service_charge > 0 && (
              <div className="flex justify-between text-sm text-brand-dark/60"><span>Online Ordering Fee</span><span>{gbp(order.service_charge)}</span></div>
            )}
            <div className="border-t pt-2 flex justify-between font-bold text-lg text-brand-dark">
              <span>Total paid</span><span>{gbp(order?.total)}</span>
            </div>
          </div>
        </div>

        <Button data-testid="order-again-btn" onClick={() => navigate("/")}
          className="w-full mt-6 h-14 rounded-2xl bg-brand-dark hover:bg-brand-dark-2 text-white font-bold">
          Order again
        </Button>
      </div>
    </div>
  );
}
